import {AxiosResponse, CancelToken} from 'axios';
import {Application} from '../backend/interfaces/application';
import {ApplicationEntity} from '../backend/responses/application-entity';
import {ApplicationUser} from '../backend/responses/application-user';
import {CategorizedApplication} from '../backend/responses/categorized-application';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export class ApplicationsRepository {
  // Singleton Instance
  private static _instance: ApplicationsRepository;

  // Private
  private logger: Logger;

  static get instance(): ApplicationsRepository {
    if (!this._instance) {
      this._instance = new ApplicationsRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('ApplicationsRepository');
  }

  // Family catalog: the entity must come from the selected senior's affiliations.
  public async getForSeniorInEntity(
    seniorId: string,
    entityId: string,
    skip: number,
    limit: number,
    name?: string,
    cancelToken?: CancelToken
  ): Promise<{data: CategorizedApplication[]; totalCount: number}> {
    this.validateSeniorContext(seniorId, entityId);
    if (
      !Number.isInteger(skip) ||
      skip < 0 ||
      !Number.isInteger(limit) ||
      limit < 1 ||
      limit > 20
    ) {
      throw new Error('Invalid application pagination');
    }
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      localize: 'true',
      categorize: 'true',
    });
    if (name?.trim()) query.set('name', name.trim());
    const response = await BackendApiManager.instance.get<CategorizedApplication[]>(
      `/users/${seniorId}/entities/${entityId}/applications?${query}`,
      {cancelToken}
    );
    const totalCount = Number(response.headers['x-total-count']);
    if (!Number.isInteger(totalCount) || totalCount < 0) {
      throw new Error('Missing or invalid application count');
    }
    return {data: response.data, totalCount};
  }

  public async getByIdForSeniorInEntity(
    seniorId: string,
    entityId: string,
    applicationId: string,
    cancelToken?: CancelToken
  ): Promise<Application> {
    this.validateSeniorContext(seniorId, entityId);
    if (!/^[a-f0-9]{24}$/i.test(applicationId)) throw new Error('Invalid application ID');
    const response = await BackendApiManager.instance.get<Application>(
      `/users/${seniorId}/entities/${entityId}/applications/${applicationId}?localize=true`,
      {cancelToken}
    );
    return response.data;
  }

  private validateSeniorContext(seniorId: string, entityId: string) {
    if (![seniorId, entityId].every((id) => /^[a-f0-9]{24}$/i.test(id))) {
      throw new Error('Invalid senior or entity ID');
    }
  }

  // Get an application by id
  public async getById(id: string, localize: boolean): Promise<Application> {
    // Set url
    let url: string = `/applications/${id}?localize=${localize.toString()}`;

    // Get application from backend
    const result: AxiosResponse<Application> =
      await BackendApiManager.instance.get<Application>(url);

    // Return
    return result.data;
  }

  // Get an application by id for a specific entity
  public async getByIdForEntity(
    entityId: string,
    applicationId: string,
    localize: boolean
  ): Promise<Application> {
    // Set url
    let url: string = `/entities/${entityId}/applications/${applicationId}?localize=${localize.toString()}`;

    // Get application from backend
    const result: AxiosResponse<Application> =
      await BackendApiManager.instance.get<Application>(url);

    // Return
    return result.data;
  }

  // Get all applications in the system as a system administrator
  public async getAsSystemAdministrator(
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<CategorizedApplication>}> {
    // Set url
    let url: string = `/applications?skip=${skip}&limit=${limit}&localize=true&categorize=true`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get applications from backend
    const response: AxiosResponse<Array<CategorizedApplication>> =
      await BackendApiManager.instance.get<Array<CategorizedApplication>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all applications in an entity as a system administrator
  public async getInEntityAsSystemAdministrator(
    entityId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<CategorizedApplication>}> {
    // Set url
    let url: string = `/applications/entities/${entityId}?skip=${skip}&limit=${limit}&localize=true&categorize=true`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get applications from backend
    const response: AxiosResponse<Array<CategorizedApplication>> =
      await BackendApiManager.instance.get<Array<CategorizedApplication>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all applications in an entity as a professional caregiver
  public async getInEntityAsAffiliatedForEntity(
    entityId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<CategorizedApplication>}> {
    // Set url
    let url: string = `/entities/${entityId}/applications?skip=${skip}&limit=${limit}&localize=true&categorize=true`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get applications from backend
    const response: AxiosResponse<Array<CategorizedApplication>> =
      await BackendApiManager.instance.get<Array<CategorizedApplication>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all applications associated with a user in an entity as a professional caregiver and system admin
  public async getForUserInEntity(
    userId: string,
    entityId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<CategorizedApplication>}> {
    // Set url
    let url: string = `/entities/${entityId}/users/${userId}/applications?skip=${skip}&limit=${limit}&localize=true&categorize=true`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get applications from backend
    const response: AxiosResponse<Array<CategorizedApplication>> =
      await BackendApiManager.instance.get<Array<CategorizedApplication>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Update the isActive property of an application for all users in an entity as a professional caregiver
  public async updateActiveStateForAllUsersAsAffiliatedForEntity(
    entityId: string,
    applicationId: string,
    flag: boolean
  ): Promise<void> {
    await BackendApiManager.instance.patch<void>(
      `/entities/${entityId}/applications/${applicationId}/active/${flag}`
    );
  }

  // Update the isActive property of an application for an entity as a system administrator
  public async updateActiveStateForEntityAsSystemAdministrator(
    applicationId: string,
    entityId: string,
    flag: boolean
  ) {
    await BackendApiManager.instance.patch<void>(
      `/applications/${applicationId}/entities/${entityId}/active/${flag}`
    );
  }

  // Update the isActive property of an application for a user as a system administrator and professional caregiver
  public async updateActiveStateForUser(
    userId: string,
    applicationId: string,
    flag: boolean
  ): Promise<void> {
    await BackendApiManager.instance.patch<void>(
      `/users/${userId}/applications/${applicationId}/active/${flag}`
    );
  }

  // Get all entities in the system associated with an application as a system administrator
  public async getApplicationEntitiesAsSystemAdministrator(
    applicationId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<ApplicationEntity>}> {
    // Set url
    let url: string = `/applications/${applicationId}/entities?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get entities from backend
    const response: AxiosResponse<Array<ApplicationEntity>> =
      await BackendApiManager.instance.get<Array<ApplicationEntity>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all users in an entity associated with an application as a professional caregiver
  public async getApplicationUsersInEntityAsAffiliatedForEntity(
    entityId: string,
    applicationId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<ApplicationUser>}> {
    // Set url
    let url: string = `/entities/${entityId}/applications/${applicationId}/users?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get users from backend
    const response: AxiosResponse<Array<ApplicationUser>> =
      await BackendApiManager.instance.get<Array<ApplicationUser>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }
}
